import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Stack,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';

import { useCreateUnitMutation, useUpdateUnitMutation } from '../unitsApi';

import { Unit } from '@/shared/types/unit';

interface UnitDialogProps {
  open: boolean;
  onClose: () => void;
  courseId: string;
  currentUserId: string;
  unit?: Unit;
  mode: 'create' | 'edit';
}

const UnitDialog: React.FC<UnitDialogProps> = ({
  open,
  onClose,
  courseId,
  currentUserId,
  unit,
  mode,
}) => {
  const [title, setTitle] = useState(unit?.title || '');
  const [description, setDescription] = useState(unit?.description || '');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const navigate = useNavigate();
  const [createUnit, { isLoading: creating }] = useCreateUnitMutation();
  const [updateUnit, { isLoading: updating }] = useUpdateUnitMutation();

  const isLoading = creating || updating;

  // Reset form when dialog opens with new data
  React.useEffect(() => {
    if (open) {
      setTitle(unit?.title || '');
      setDescription(unit?.description || '');
      setErrorMsg(null);
    }
  }, [open, unit]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!title.trim()) {
      setErrorMsg('Unit title is required.');
      return;
    }

    setErrorMsg(null);

    try {
      if (mode === 'create') {
        const newUnit = await createUnit({
          title: title.trim(),
          description: description.trim(),
          owner_id: currentUserId,
          course_id: courseId,
        }).unwrap();
        onClose();
        navigate(`/curriculum/courses/${courseId}/units/${newUnit.id}`);
        return;
      } else if (unit) {
        await updateUnit({
          id: unit.id,
          title: title.trim(),
          description: description.trim(),
        }).unwrap();
      }

      onClose();
    } catch (error) {
      console.error('Unit operation error:', error);
      setErrorMsg(`Failed to ${mode} unit. Please try again.`);
    }
  };

  const handleClose = () => {
    setTitle(unit?.title || '');
    setDescription(unit?.description || '');
    setErrorMsg(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit}>
        <DialogTitle>{mode === 'create' ? 'Create New Unit' : 'Edit Unit'}</DialogTitle>

        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <TextField
              label="Unit Title*"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              fullWidth
              autoFocus
              disabled={isLoading}
            />

            <TextField
              label="Description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              fullWidth
              multiline
              rows={3}
              disabled={isLoading}
            />

            {errorMsg && <Alert severity="error">{errorMsg}</Alert>}
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" disabled={isLoading || !title.trim()}>
            {isLoading ? (
              <CircularProgress size={20} />
            ) : mode === 'create' ? (
              'Create Unit'
            ) : (
              'Save Changes'
            )}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default UnitDialog;
